import React, { useCallback, useEffect, useState } from "react";

import { useLocalization } from "@fluent/react";
import { IconButton } from "@narmi/design_system";

import styles from "./ReceiptDetailsDrawer.module.scss";

interface ReceiptImagePreviewProps {
  imageUrl: string;
  mimeType?: string | null;
  thumbnailUrl?: string | null;
  largeThumbnailUrl?: string | null;
}

const ReceiptImagePreview = ({
  imageUrl,
  mimeType,
  thumbnailUrl,
  largeThumbnailUrl,
}: ReceiptImagePreviewProps) => {
  const { l10n } = useLocalization();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const openFullscreen = useCallback(() => setIsFullscreen(true), []);
  const closeFullscreen = useCallback(() => setIsFullscreen(false), []);

  // Capture ESC at the overlay level and stop it from bubbling to the drawer
  // (NDS Drawer dismisses on ESC at the window level).
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setIsFullscreen(false);
      }
    },
    []
  );

  // Ensure the overlay receives focus so keyboard events target it.
  useEffect(() => {
    if (!isFullscreen) return undefined;
    const overlay = document.getElementById("receipt-image-fullscreen-root");
    overlay?.focus();
    return undefined;
  }, [isFullscreen]);

  const alt = l10n.getString("receipts-drawer-image-alt");
  const isPdf = mimeType === "application/pdf";
  // Best-effort chrome hide for native PDF viewers (Chrome respects most of
  // these, Firefox/Safari ignore some). Fragment is safe to append to a
  // presigned URL — query params are signed, fragments are not.
  const pdfSrc = isPdf
    ? `${imageUrl}#toolbar=0&navpanes=0&scrollbar=0`
    : imageUrl;

  const renderPreview = () => {
    if (thumbnailUrl) {
      return (
        <img
          src={thumbnailUrl}
          alt={alt}
          className={styles["image-preview-img"]}
          data-testid="receipt-image-preview"
        />
      );
    }
    if (isPdf) {
      return (
        <embed
          src={pdfSrc}
          type="application/pdf"
          title={alt}
          aria-label={alt}
          className={styles["image-preview-embed"]}
          data-testid="receipt-image-preview"
        />
      );
    }
    return (
      <img
        src={imageUrl}
        alt={alt}
        className={styles["image-preview-img"]}
        data-testid="receipt-image-preview"
      />
    );
  };

  const renderFullscreen = () => {
    // For PDFs, maximize embeds the real document so all pages and vector zoom
    // are available — the large thumbnail is only page one. The thumbnail still
    // backs the collapsed preview (renderPreview) where speed matters more.
    if (isPdf) {
      return (
        <embed
          src={pdfSrc}
          type="application/pdf"
          title={alt}
          aria-label={alt}
          className={styles["fullscreen-img"]}
        />
      );
    }
    if (largeThumbnailUrl) {
      return (
        <img
          src={largeThumbnailUrl}
          alt={alt}
          className={styles["fullscreen-img"]}
        />
      );
    }
    // Defense-in-depth: a display thumbnail is always a browser-renderable JPEG,
    // whereas the original may not be (e.g. HEIC). The detail serializer derives
    // both tiers from the same key so this shouldn't trigger today, but prefer
    // the thumbnail over the original if they ever diverge.
    if (thumbnailUrl) {
      return (
        <img
          src={thumbnailUrl}
          alt={alt}
          className={styles["fullscreen-img"]}
        />
      );
    }
    return (
      <img src={imageUrl} alt={alt} className={styles["fullscreen-img"]} />
    );
  };

  return (
    <div className={styles["image-preview"]}>
      <div className={styles["image-preview-wrapper"]}>
        {renderPreview()}
        <div className={styles["image-preview-maximize"]}>
          <IconButton
            kind="action"
            name="maximize"
            label={l10n.getString("receipts-drawer-image-maximize")}
            onClick={openFullscreen}
            testId="receipt-image-maximize"
          />
        </div>
      </div>
      {isFullscreen && (
        <div
          id="receipt-image-fullscreen-root"
          className={styles.fullscreen}
          role="dialog"
          aria-modal="true"
          aria-label={alt}
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          data-testid="receipt-image-fullscreen"
        >
          <div className={styles["fullscreen-close"]}>
            <IconButton
              kind="action"
              name="x"
              label={l10n.getString("receipts-drawer-image-close")}
              onClick={closeFullscreen}
              testId="receipt-image-fullscreen-close"
            />
          </div>
          {renderFullscreen()}
        </div>
      )}
    </div>
  );
};

export default ReceiptImagePreview;
